'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard,
  Calendar,
  TrendingUp,
  Store,
  Scissors,
  Clock,
  Package,
  Sliders,
  Users,
  MessageSquare,
  Settings,
  Gift,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  LogOut,
  Search,
  X
} from 'lucide-react';
import { CalenddyLogo } from '@/components/brand/CalenddyLogo';

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  businessSlug?: string;
  businessName?: string;
}

export function Sidebar({
  isOpen,
  onClose,
  businessSlug = 'juanrojas',
  businessName = 'Juan Rojas'
}: SidebarProps) {
  const pathname = usePathname();
  const [negocioOpen, setNegocioOpen] = useState(true);

  const isItemActive = (href: string) => {
    if (href === '/dashboard') return pathname === '/dashboard';
    return pathname.startsWith(href);
  };

  const negocioActive = [
    '/dashboard/event-types',
    '/dashboard/availability',
    '/dashboard/products',
    '/dashboard/settings/booking-rules'
  ].some((h) => pathname.startsWith(h));

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div
          onClick={onClose}
          className="fixed inset-0 bg-black/40 z-40 md:hidden backdrop-blur-xs transition-opacity duration-200"
          aria-hidden="true"
        />
      )}

      {/* Sidebar Container: Fondo Blanco con Acentos Turquesa #00A896 */}
      <aside
        className={`
          fixed md:sticky top-0 left-0 h-screen w-64 bg-white border-r border-gray-200/90 text-gray-900 z-50 flex flex-col justify-between shrink-0 transition-transform duration-200 ease-out font-[family-name:var(--font-plus-jakarta)] shadow-2xs
          ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
      >
        {/* Top: Logo & Search */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="h-16 px-5 flex items-center justify-between shrink-0 border-b border-gray-100 bg-white">
            <CalenddyLogo variant="dark" size="sm" href="/dashboard" />

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-[#00A896] hover:bg-[#F0FAF9] transition-colors cursor-pointer"
                title="Buscar en la plataforma"
                onClick={() => {
                  const searchEl = document.querySelector('input[type="search"], input[placeholder*="Buscar"]') as HTMLInputElement;
                  if (searchEl) searchEl.focus();
                }}
              >
                <Search className="w-4.5 h-4.5" />
              </button>

              <button
                type="button"
                onClick={onClose}
                className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
                title="Cerrar menú"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation Scrollable Area */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5 scrollbar-thin scrollbar-thumb-gray-200">
            {/* Inicio */}
            <Link
              href="/dashboard"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group
                ${
                  pathname === '/dashboard'
                    ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                    : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                }
              `}
            >
              <LayoutDashboard
                className={`w-5 h-5 shrink-0 transition-colors ${
                  pathname === '/dashboard' ? 'text-[#00A896]' : 'text-gray-400 group-hover:text-[#00A896]'
                }`}
              />
              <span className="truncate">Inicio</span>
            </Link>

            {/* Reservas */}
            <Link
              href="/dashboard/bookings"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group
                ${
                  pathname.startsWith('/dashboard/bookings')
                    ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                    : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                }
              `}
            >
              <Calendar
                className={`w-5 h-5 shrink-0 transition-colors ${
                  pathname.startsWith('/dashboard/bookings')
                    ? 'text-[#00A896]'
                    : 'text-gray-400 group-hover:text-[#00A896]'
                }`}
              />
              <span className="truncate">Reservas</span>
            </Link>

            {/* Ventas */}
            <Link
              href="/dashboard/sales"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group
                ${
                  pathname.startsWith('/dashboard/sales')
                    ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                    : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                }
              `}
            >
              <TrendingUp
                className={`w-5 h-5 shrink-0 transition-colors ${
                  pathname.startsWith('/dashboard/sales')
                    ? 'text-[#00A896]'
                    : 'text-gray-400 group-hover:text-[#00A896]'
                }`}
              />
              <span className="truncate">Ventas</span>
            </Link>

            {/* Negocio (Desplegable) */}
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setNegocioOpen(!negocioOpen)}
                className={`
                  w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group cursor-pointer
                  ${
                    negocioActive
                      ? 'text-[#005C52] font-semibold bg-[#F0FAF9]'
                      : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                  }
                `}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Store
                    className={`w-5 h-5 shrink-0 transition-colors ${
                      negocioActive ? 'text-[#00A896]' : 'text-gray-400 group-hover:text-[#00A896]'
                    }`}
                  />
                  <span className="truncate">Negocio</span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 group-hover:text-[#00A896] transition-transform duration-200 ${
                    negocioOpen ? 'rotate-0' : '-rotate-90'
                  }`}
                />
              </button>

              {/* Subitems indentados de Negocio */}
              {negocioOpen && (
                <div className="mt-1 pl-4 space-y-1">
                  <Link
                    href="/dashboard/event-types"
                    onClick={onClose}
                    className={`
                      flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 group
                      ${
                        pathname.startsWith('/dashboard/event-types')
                          ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                          : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                      }
                    `}
                  >
                    <Scissors className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-[#00A896]" />
                    <span className="truncate">Servicios</span>
                  </Link>

                  <Link
                    href="/dashboard/availability"
                    onClick={onClose}
                    className={`
                      flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 group
                      ${
                        pathname.startsWith('/dashboard/availability')
                          ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                          : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                      }
                    `}
                  >
                    <Clock className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-[#00A896]" />
                    <span className="truncate">Horarios</span>
                  </Link>

                  <Link
                    href="/dashboard/products"
                    onClick={onClose}
                    className={`
                      flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 group
                      ${
                        pathname.startsWith('/dashboard/products')
                          ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                          : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                      }
                    `}
                  >
                    <Package className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-[#00A896]" />
                    <span className="truncate">Productos</span>
                  </Link>

                  <Link
                    href="/dashboard/settings/booking-rules"
                    onClick={onClose}
                    className={`
                      flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 group
                      ${
                        pathname.startsWith('/dashboard/settings/booking-rules')
                          ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                          : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                      }
                    `}
                  >
                    <Sliders className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-[#00A896]" />
                    <span className="truncate">Reglas de agenda</span>
                  </Link>
                </div>
              )}
            </div>

            {/* Separador de Sección */}
            <div className="pt-3 pb-1">
              <p className="px-3 text-[11px] font-bold text-gray-400 uppercase tracking-wider select-none">
                Gestión & Clientes
              </p>
            </div>

            {/* Clientes CRM */}
            <Link
              href="/dashboard/clients"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group
                ${
                  pathname.startsWith('/dashboard/clients')
                    ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                    : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                }
              `}
            >
              <Users
                className={`w-5 h-5 shrink-0 transition-colors ${
                  pathname.startsWith('/dashboard/clients')
                    ? 'text-[#00A896]'
                    : 'text-gray-400 group-hover:text-[#00A896]'
                }`}
              />
              <span className="truncate">Clientes CRM</span>
            </Link>

            {/* WhatsApp */}
            <Link
              href="/dashboard/whatsapp"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group
                ${
                  pathname.startsWith('/dashboard/whatsapp')
                    ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                    : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                }
              `}
            >
              <MessageSquare
                className={`w-5 h-5 shrink-0 transition-colors ${
                  pathname.startsWith('/dashboard/whatsapp')
                    ? 'text-[#00A896]'
                    : 'text-gray-400 group-hover:text-[#00A896]'
                }`}
              />
              <span className="truncate">WhatsApp</span>
            </Link>

            {/* Configuración */}
            <Link
              href="/dashboard/settings"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group
                ${
                  pathname.startsWith('/dashboard/settings') && !pathname.includes('booking-rules')
                    ? 'bg-[#E6F6F4] text-[#005C52] font-semibold border border-[#00A896]/20 shadow-xs'
                    : 'text-gray-600 hover:text-[#005C52] hover:bg-[#F0FAF9]'
                }
              `}
            >
              <Settings
                className={`w-5 h-5 shrink-0 transition-colors ${
                  pathname.startsWith('/dashboard/settings') && !pathname.includes('booking-rules')
                    ? 'text-[#00A896]'
                    : 'text-gray-400 group-hover:text-[#00A896]'
                }`}
              />
              <span className="truncate">Configuración</span>
            </Link>
          </div>
        </div>

        {/* Bottom Section: Trial pill, Business link, User footer */}
        <div className="shrink-0 p-3 border-t border-gray-100 space-y-2.5 bg-gray-50/60">
          {/* Trial / Promo Card en Turquesa */}
          <Link
            href="/dashboard/settings/subscription"
            onClick={onClose}
            className="w-full bg-gradient-to-r from-[#F0FAF9] to-[#E6F6F4] hover:from-[#E6F6F4] hover:to-[#CCF0EC] border border-[#00A896]/30 rounded-2xl p-2.5 flex items-center justify-between text-gray-900 transition-all group shadow-2xs"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-xl bg-[#00A896]/15 flex items-center justify-center shrink-0 text-[#00A896]">
                <Gift className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <span className="text-xs font-semibold block truncate leading-tight text-gray-900">
                  Prueba gratis · 14 días
                </span>
                <span className="text-[11px] text-[#00A896] font-bold block truncate leading-tight">
                  Activala con 40% OFF
                </span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#00A896] group-hover:translate-x-0.5 transition-transform shrink-0" />
          </Link>

          {/* Business Public Link (JR Pill) */}
          <Link
            href={`/${businessSlug}`}
            target="_blank"
            className="w-full bg-white hover:bg-[#F0FAF9] border border-gray-200 hover:border-[#00A896]/40 rounded-xl px-3 py-2 flex items-center justify-between text-gray-800 text-xs font-medium transition-all group shadow-2xs"
            title="Abrir página pública de reservas"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 rounded-full bg-[#00A896] text-white font-bold text-[11px] flex items-center justify-center uppercase shrink-0">
                {businessName.slice(0, 2)}
              </div>
              <span className="font-semibold truncate">{businessName}</span>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-gray-400 group-hover:text-[#00A896] transition-colors shrink-0" />
          </Link>

          {/* User profile & logout button */}
          <div className="flex items-center justify-between px-1 pt-0.5">
            <Link
              href="/dashboard/settings"
              onClick={onClose}
              className="flex items-center gap-2 min-w-0 text-gray-700 hover:text-[#00A896] transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-700 border border-gray-200 flex items-center justify-center text-xs font-bold uppercase shrink-0">
                {businessName.slice(0, 1)}
              </div>
              <span className="text-xs font-semibold truncate">{businessName}</span>
            </Link>

            <form action="/api/auth/logout" method="POST">
              <button
                type="submit"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                title="Cerrar sesión"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>
        </div>
      </aside>
    </>
  );
}
